/* eslint-disable eqeqeq */
import React, { useMemo, useRef, useState } from "react";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { toast } from "@iventis/toasts/src/toast";
import { Theme } from "@emotion/react";
import { greyTheme, styled } from "@iventis/styles";
import { useTheme } from "@mui/material";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ColumnProps,
    DataTableCallbackRefs,
    GridMachineContext,
    TableControls,
    RowNetworkStatus,
    QueueInsert,
    SubheaderListItem,
    QueueEdit,
    QueueDelete,
    DataTableOperations,
} from "@iventis/data-table";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { convertDataFieldsToColumns, generateListColumn, generateSubheaderListColumn, generateTextColumn } from "@iventis/data-table/helpers/column-generators";
import { Content } from "@iventis/translations";

import { isNullUndefinedOrEmptyGuid } from "@iventis/utilities";
import { v4 as uuid } from "uuid";
import { DataTable } from "@iventis/data-table/components/data-table";
import {
    getVenueChildren,
    isParentIdFieldValid,
    isTopLevelVenue,
    rowToVenueLocation,
    topLevelVenueOption,
    topLevelVenueOptionId,
    VenueLocationParentIdCellEditor,
    VenueLocationRowData,
    VenueLocationRowNode,
} from "./edit-venue-location-helpers";

export const EditVenueLocations: React.FC<{ venueService: VenueLocationsService; venueLocationTypes: ScheduleConfigVenueLocationType[]; dataFields: DataField[] }> = ({
    venueService,
    venueLocationTypes,
    dataFields,
}) => {
    const venueLocationsQuery = useQuery([venueLocationsKey], async () => flattenVenueLocationHierarchies(await venueService.getVenueLocations()));
    const queryClient = useQueryClient();
    const translate = useIventisTranslate();

    const theme = useTheme<Theme>();

    const venueLocations = useMemo(() => {
        if (venueLocationsQuery.status === "success") {
            return venueLocationsQuery.data.map((d) => ({ ...d, canEdit: true, networkStatus: RowNetworkStatus.ExistsOnServer }));
        }
        return undefined;
    }, [venueLocationsQuery.data]);

    // Get datafields that our current venue location types are using
    const validDatafields = useMemo(
        () =>
            venueLocationTypes?.reduce(
                (cum, { dataFields: dataFieldIds }) => [
                    ...cum,
                    ...dataFields.reduce((dataFieldsCum, dataField) => {
                        // Check if we already have this datafield saved and that the datafield is connected to this type
                        if (!cum.some((df) => df.id === dataField.id) && dataFieldIds.includes(dataField.id)) {
                            return [...dataFieldsCum, dataField];
                        }
                        return dataFieldsCum;
                    }, [] as DataField[]),
                ],
                [] as DataField[]
            ),
        [venueLocationTypes]
    );

    const dataTableRef = useRef<DataTableCallbackRefs<VenueLocationRowData>>();

    const [selectedRow, setSelectedRow] = useState<VenueLocationRowNode>();

    const allVenueLocationOptions = useMemo(() => [topLevelVenueOption, ...(venueLocations || [])], [venueLocations]);

    const venueLocationOptionsHierarchy: SubheaderListItem[] = useMemo(() => {
        if (!venueLocations || !venueLocationTypes) {
            return [];
        }
        const categorised: SubheaderListItem[] = venueLocationTypes.map(({ name, id: venueLocationTypeId }) => ({
            title: name,
            listItems: venueLocations.filter((venueLocation) => venueLocation.type.id === venueLocationTypeId),
        }));

        return [{ title: translate(Content.schedule3.venueEdit.topLevel), listItems: [topLevelVenueOption] }, ...categorised];
    }, [venueLocations, venueLocationTypes]);

    const bottomVenueLocationType = useMemo(() => venueLocationTypes.find((t) => !venueLocationTypes.some((otherT) => otherT.allowedParentType === t.id)), [venueLocationTypes]);

    const columns: ColumnProps[] = useMemo(
        () => [
            generateTextColumn({ field: "name", headerName: translate(Content.common.name) }),
            generateSubheaderListColumn(
                {
                    // Validation against empty values?
                    componentParams: { sections: venueLocationOptionsHierarchy, listItems: allVenueLocationOptions },
                    // Do not show options for lowest-level venue location types
                    filterOptions: (items, node) =>
                        items.filter((parentOption) => {
                            const currentRowData = node.data as VenueLocationRowData;
                            const parentLocation = venueLocations.find((location) => location.id === parentOption.id);

                            // If there is already a selected type on this row, only show parent options for the existing parent type's level
                            if (currentRowData.type !== undefined) {
                                if (isNullUndefinedOrEmptyGuid(parentOption.id)) {
                                    // Parent option is top level, only allow the top level option
                                    return isNullUndefinedOrEmptyGuid(currentRowData.type.allowedParentType);
                                }
                                return currentRowData.type.allowedParentType === parentLocation.type.id;
                            }

                            // Type is not already selected from here onwards

                            if (isNullUndefinedOrEmptyGuid(parentOption.id)) {
                                // Allow top level selection
                                return true;
                            }

                            // Prevent selection of a parent which is already at the bottommost level
                            return parentLocation.type.id !== bottomVenueLocationType.id;
                        }),
                },
                {
                    field: "parentId",
                    headerName: translate(Content.schedule2.venueEdit.parentVenue),
                    valueGetter: (value: { data: VenueLocationRowData }) => {
                        if (value.data.type === undefined) {
                            // No value for the type has been selected yet, parent ID can't have been selected either
                            return undefined;
                        }

                        if (isTopLevelVenue(value.data)) {
                            return topLevelVenueOptionId;
                        }

                        if (isNullUndefinedOrEmptyGuid(value.data.parentId)) {
                            // No value selected yet
                            return undefined;
                        }

                        return value.data.parentId;
                    },
                    cellEditor: VenueLocationParentIdCellEditor,
                }
            ),
            generateListColumn(
                { componentParams: { listItems: venueLocationTypes } },
                { field: "type", headerName: translate(Content.schedule2.venueEdit.venueLocationType) },
                { valueGetter: ({ data }) => data.type?.id, editable: false }
            ),
            ...convertDataFieldsToColumns(validDatafields ?? [], {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                cellEditable: (rowData: any, datafieldId) => rowData.type.dataFields.some((dfId) => dfId === datafieldId),
                valueGetter: (dataFieldId) => (params) => params.data.dataFieldValues.find((dataFieldValue) => dataFieldValue.dataField.id === dataFieldId)?.value,
                valueSetter: (params) => {
                    // try to change the datafield if its already stored
                    try {
                        const dataFieldId = params.column.getColId();
                        // eslint-disable-next-line no-param-reassign
                        params.data.dataFieldValues.find((dataFieldValue) => dataFieldValue.dataField.id === dataFieldId).value = params.newValue;
                        return true;
                    } catch {
                        // If not try add the datafield to the row
                        try {
                            const dataField = dataFields.find(({ id }) => id === params.column.getColId());
                            // eslint-disable-next-line no-param-reassign
                            params.data.dataFieldValues = [...params.data.dataFieldValues, { dataField, value: params.newValue }] as DataFieldValue[];
                            return true;
                        } catch {
                            // If all that failed then we couldnt set the value for whatever reason
                            return false;
                        }
                    }
                },
            }),
        ],
        [venueLocations, translate]
    );

    const insertNewrow = () => {
        const row = CreateNewVenueLocationRow();
        dataTableRef.current.insertRow([row], 0);
    };

    const createVenueFromRow = async (context: GridMachineContext<VenueLocationRowData>) => {
        const { node } = context.queue[0] as QueueInsert;
        const location = await venueService.createVenueLocation(rowToVenueLocation(node.data as VenueLocationRowData));
        dataTableRef.current
            .getApi()
            .getRowNode(node.id)
            .setData({ ...location, canEdit: true, networkStatus: RowNetworkStatus.ExistsOnServer });
        queryClient.setQueryData<VenueLocation[]>([venueLocationsKey], (oldData) => [location, ...oldData]);
        return location;
    };

    const editVenueFromRow = async (context: GridMachineContext<VenueLocationRowData>) => {
        const { node } = context.queue[0] as QueueEdit<VenueLocationRowData>;
        const location = await venueService.updateVenueLocation(rowToVenueLocation(node.data as VenueLocationRowData));
        queryClient.setQueryData<VenueLocation[]>([venueLocationsKey], (oldData) => oldData.map((venue) => (venue.id === location.id ? location : venue)));
        return location;
    };

    const deleteVenueFromRow = async (context: GridMachineContext<VenueLocationRowData>) => {
        const row = (context.queue[0] as QueueDelete<VenueLocationRowData>)?.row;
        await venueService.deleteVenueLocation(rowToVenueLocation(row));
        queryClient.setQueryData<VenueLocation[]>([venueLocationsKey], (oldData) => oldData.filter((venue) => venue.id !== row.id));
        return row;
    };

    const deleteRow = () => {
        dataTableRef.current.deleteRequest();
    };

    // All non-top-level venues must have a parent ID
    return (
        <StyledContainer>
            <TableControls addNewRow={insertNewrow} deleteRow={deleteRow} rowSelected={selectedRow != undefined} canDelete />
            {venueLocations && (
                <DataTable<VenueLocationRowData>
                    requiredFields={["name", "parentId"]}
                    ref={dataTableRef}
                    theme={greyTheme(theme)}
                    gridOptions={{ rowData: venueLocations, context: { translate, toast }, headerHeight: 30, rowHeight: 36 }}
                    columns={columns}
                    validateRow={(row) => isParentIdFieldValid(row)}
                    sideEffectsOnCellValueChanged={(v) => {
                        // Set the venue location type based on the chosen parent ID
                        const { node, colDef } = v;
                        const { data } = node;
                        if (colDef.field === "parentId") {
                            if (data.parentId === topLevelVenueOptionId) {
                                // If the parent ID is the top level, the venue location type is the top level type
                                node.setData({ ...data, type: venueLocationTypes.find((t) => isNullUndefinedOrEmptyGuid(t.allowedParentType)) });
                                return;
                            }
                            const { type: parentType } = venueLocations.find((l) => l.id === (data.parentId as string));
                            const rowType = venueLocationTypes.find((t) => t.allowedParentType === parentType.id);
                            node.setData({ ...data, type: rowType });
                        }
                    }}
                    onRowSelected={(e) => setSelectedRow(e[0])}
                    onDeleteRequested={(rows) => {
                        const oneVenueHasChildren = rows.some((row) => getVenueChildren(venueLocations, row.id).length > 0);
                        if (oneVenueHasChildren) {
                            toast.warning({ title: translate(Content.common.warning), message: translate(Content.schedule2.venueEdit.tryDeleteParent) });
                            dataTableRef.current.deleteCancelled();
                            return;
                        }
                        dataTableRef.current.deleteConfirmed();
                    }}
                    onNetworkRequestError={({ operation }) => {
                        if (operation === DataTableOperations.Insert) {
                            toast.error({ title: translate(Content.errors.error), message: translate(Content.schedule3.venueEdit.insertFailure) });
                        }
                        if (operation === DataTableOperations.Edit) {
                            toast.error({ title: translate(Content.errors.error), message: translate(Content.schedule3.venueEdit.editFailure) });
                        }
                        if (operation === DataTableOperations.Delete) {
                            toast.error({ title: translate(Content.errors.error), message: translate(Content.schedule3.venueEdit.deleteFailure) });
                        }
                    }}
                    onNetworkRequestSuccess={({ operation }) => {
                        if (operation === DataTableOperations.Insert) {
                            toast.success({ title: translate(Content.common.success), message: translate(Content.schedule3.venueEdit.creationSuccess) });
                        }
                        if (operation === DataTableOperations.Edit) {
                            toast.success({ title: translate(Content.common.success), message: translate(Content.schedule3.venueEdit.editSuccess) });
                        }
                        if (operation === DataTableOperations.Delete) {
                            toast.success({ title: translate(Content.common.success), message: translate(Content.schedule3.venueEdit.deleteSuccess) });
                        }
                    }}
                    insertRowAsync={createVenueFromRow}
                    editRowAsync={editVenueFromRow}
                    deleteRowAsync={deleteVenueFromRow}
                />
            )}
        </StyledContainer>
    );
};

const StyledContainer = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: stretch;
    align-items: stretch;

    .ag-grid-iventis {
        flex: 1;
    }
`;

export interface VenueLocationsService {
    getVenueLocations: () => Promise<Hierarchy<VenueLocation>[]>;
    updateVenueLocation: (venueLocation: VenueLocation) => Promise<VenueLocation>;
    createVenueLocation: (venueLocation: VenueLocation) => Promise<VenueLocation>;
    deleteVenueLocation: (venueLocation: VenueLocation) => Promise<VenueLocation>;
}

export const venueLocationsKey = "VENUE_LOCATIONS";

export const flattenVenueLocationHierarchies = (venueLocationHierarchies: Hierarchy<VenueLocation>[]): VenueLocation[] =>
    venueLocationHierarchies.reduce<VenueLocation[]>((cum, vlh) => [...cum, vlh.item, ...flattenVenueLocationHierarchies(vlh.children || [])], [] as VenueLocation[]);

export const CreateNewVenueLocationRow = () =>
    ({
        name: undefined,
        id: uuid(),
        scheduleId: undefined,
        parentId: undefined,
        type: undefined,
        city: undefined,
        dataFieldValues: [],
        canEdit: true,
        networkStatus: RowNetworkStatus.PendingCreation,
    } as VenueLocationRowData);
